import { formatRFC3339 } from "date-fns"
import { v4 } from "uuid"
import type { Domain as DomainTypes } from "./domain"

export interface EventStruct<
  Domain extends DomainTypes,
  EventName extends string,
  Context extends Record<string, unknown>
> {
  readonly id: string
  readonly domain: Domain
  readonly eventName: EventName
  readonly context: Context
  readonly timestamp: string
  readonly contentId?: string
}

export abstract class EventBase<
  Domain extends DomainTypes,
  EventName extends string,
  Context extends Record<string, unknown> = Record<string, unknown>
> {
  readonly #id: string
  readonly #context: Context
  readonly #timestamp: string
  readonly #contentId?: string

  public constructor({
    context,
    contentId,
  }: {
    context: Context
    contentId?: string
  }) {
    this.#id = v4()
    this.#timestamp = formatRFC3339(Date.now())
    this.#context = context
    this.#contentId = contentId
  }

  public abstract get domain(): Domain
  public abstract get eventName(): EventName

  public get id(): string {
    return this.#id
  }

  public get context(): Context {
    return this.#context
  }

  public get timestamp(): string {
    return this.#timestamp
  }

  public get contentId(): string | undefined {
    return this.#contentId
  }

  public marshal(): string {
    return JSON.stringify({
      id: this.id,
      domain: this.domain,
      eventName: this.eventName,
      context: this.context,
      timestamp: this.timestamp,
      contentId: this.contentId,
    } satisfies EventStruct<Domain, EventName, Context>)
  }

  public get eventNameSnakeCase(): string {
    return (
      this.eventName
        // Insert underscore before uppercase letters that follow lowercase letters
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        // Insert underscore before uppercase letters that follow numbers
        .replace(/([0-9])([A-Z])/g, "$1_$2")
        // Replace spaces, hyphens, and other separators with underscores
        .replace(/[\s\-\.]+/g, "_")
        // Remove leading/trailing underscores
        .replace(/^_+|_+$/g, "")
        .toLowerCase()
    )
  }

  public get eventNameKababCase(): string {
    return this.eventName.replace(/ /g, "-").toLowerCase()
  }
}
